// Libs
import classNames from 'classnames/bind';
import { Button, Flex, Heading, IconRefreshLine, Spinner, Table, Text, Tooltip } from '@instructure/ui';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';

// Components, Layouts, Pages
import Information from '~/pages/student/home/component/information/Information';
import { BasePagination } from '~/components/common';
// Others
import { TuitionStatusEnum } from '~/utils/enum/base';
import { getListTuitions } from '~/thunks/tuition/tuitionThunk';
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { DEFAULT_LIMIT_PAGE, DEFAULT_PAGE } from '~/utils/constants/common';
import { IQueryGetTuitions } from '~/utils/interface/tuition';
// Styles, images, icons
import styles from './TuitionFees.module.scss';
import { icons } from '~/assets';

type Props = {
  content?: string;
};

const cx = classNames.bind(styles);

const TuitionFees = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/home', 'common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  //#endregion Declare Hook

  //#region Selector
  const { tuitions, currentPage, hasMore, isLoading } = useAppSelector((state) => state.tuitionState);
  //#endregion Selector

  //#region Declare State
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [page, setPage] = useState<number>(DEFAULT_PAGE);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetTuitions();
  }, []);
  //#endregion Implement Hook

  //#region Handle Function
  const handleChangePage = (newPage: number) => {
    setPage(newPage);
    handleGetTuitions({
      limit: DEFAULT_LIMIT_PAGE,
      page: newPage,
    });
  };

  const handleGetTuitions = async (query?: IQueryGetTuitions) => {
    setErrorMessage(null);

    try {
      await dispatch(
        getListTuitions(
          query ?? {
            limit: DEFAULT_LIMIT_PAGE,
            page: page,
          }
        )
      ).unwrap();
    } catch (e) {
      setErrorMessage((e as Error).message);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className={cx('centerContainer')}>
          <Spinner size='small' />
        </div>
      );
    }

    if (errorMessage) {
      return (
        <div className={cx('centerContainer')}>
          <Flex justifyItems='center' margin='medium 0'>
            <Button
              onClick={() => handleGetTuitions()}
              size='medium'
              color='primary'
              margin='x-large 0 0 0'
              renderIcon={<IconRefreshLine />}
            >
              {t('retry_button_label', { ns: 'common/base' })}
            </Button>
          </Flex>
        </div>
      );
    }

    if (tuitions.length === 0) {
      return (
        <div className={cx('centerContainer')}>
          <Flex justifyItems='center' alignItems='center' margin='small 0 0 0'>
            <Text variant='contentSmall'>{t('tuition_fees_no_data_available', { ns: 'common/home' })}</Text>
          </Flex>
        </div>
      );
    }

    return (
      <Table layout='auto'>
        <Table.Head>
          <Table.Row>
            <Table.ColHeader id='Name' width={'65%'}>
              {t('tuition_fees_name_field_title')}
            </Table.ColHeader>
            <Table.ColHeader id='Status' width={'35%'}>
              {t('tuition_fees_status_field_title')}
            </Table.ColHeader>
          </Table.Row>
        </Table.Head>

        <Table.Body>
          {tuitions?.map((tuition, index) => (
            <Table.Row key={index}>
              <Table.Cell>
                <div className={cx('tuitionName')}>
                  <img
                    src={icons.commonIconTuitionFees}
                    alt={t('common_alt_img', { ns: 'common/base' })}
                    className={cx('iconTable')}
                  />
                  <Tooltip renderTip={tuition.name}>
                    <span>{tuition.name}</span>
                  </Tooltip>
                </div>
              </Table.Cell>

              <Table.Cell>
                <div className={cx('tuitionStatus')}>
                  <img
                    src={icons.commonIconCalendarDay}
                    alt={t('common_alt_img', { ns: 'common/base' })}
                    className={cx('tuitionStatusIcon')}
                  />
                  <Tooltip
                    renderTip={
                      tuition.status === TuitionStatusEnum.PAID
                        ? `${t('tuition_fees_paid_on_title', { ns: 'common/home' })} ${tuition.paidDate}`
                        : t('tuition_fees_unpaid_title')
                    }
                  >
                    <span className={cx('tuitionStatusText')}>
                      {tuition.status === TuitionStatusEnum.PAID
                        ? `${t('tuition_fees_paid_on_title', { ns: 'common/home' })} ${tuition.paidDate}`
                        : t('tuition_fees_unpaid_title')}
                    </span>
                  </Tooltip>
                </div>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    );
  };
  //#endregion Handle Function

  return (
    <div className={cx('viewPort')}>
      <div className={cx('backContainer')} onClick={handleBack}>
        <img src={icons.commonIconBack} alt={t('common_alt_img', { ns: 'common/base' })} className={cx('iconBack')} />
        <Text variant='content'>{t('common_back_button', { ns: 'common/base' })}</Text>
      </div>

      <div className={cx('container')}>
        <div className={cx('leftSection')}>
          <div className={cx('topBox')}>{<Information hideIcons={true} />}</div>
        </div>

        <div className={cx('rightSection')}>
          <Heading level='h3' margin='0 0 small 0'>
            {t('tuition_fees_title')}
          </Heading>

          <div className={cx('tableContainer')}>
            {renderContent()}

            <BasePagination currentPage={page} hasMore={hasMore} onChange={handleChangePage} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TuitionFees;
